// =============================================================================
// FILE: src/store/useAppStore.js
// MODULE: State — Global App Store
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   Zustand store for client-side global state. Holds the current user session,
//   role, tenant, and subscription — the four things almost every component needs.
//
// WHAT LIVES HERE vs TanStack Query:
//   Store (Zustand) → identity state that doesn't expire and doesn't paginate:
//     who is the user, what role, what tenant, what subscription tier.
//   Query (TanStack) → server state that needs caching, refetching, pagination:
//     tournaments, teams, matches, stats, standings.
//
// USAGE:
//   const { user, role, tenant } = useAppStore()
//   const { setUser, clearSession } = useAppStore()
//
// IMPORTANT:
//   Call clearSession() on every logout path — it wipes all state so the
//   next user who logs in on the same browser never sees stale data.
// =============================================================================

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// ---------------------------------------------------------------------------
// Store definition
// Using devtools middleware for Redux DevTools support in development.
// Wrap with immer if state mutations become complex (future consideration).
// ---------------------------------------------------------------------------
export const useAppStore = create(
  devtools(
    (set) => ({

      // -----------------------------------------------------------------------
      // AUTH STATE
      // Set by AuthProvider on session load / change.
      // -----------------------------------------------------------------------

      /** Supabase auth.User object — null when logged out */
      user: null,

      /** User's role from public.users — null until profile loaded */
      role: null,

      /** Full user profile row from public.users — null until loaded */
      profile: null,

      /** Whether the auth session has been checked on this page load */
      sessionChecked: false,

      // -----------------------------------------------------------------------
      // TENANT STATE
      // Set by AuthProvider after profile is loaded.
      // -----------------------------------------------------------------------

      /** Current tenant row (id, name, slug) — null for super_admin or unloaded */
      tenant: null,

      // -----------------------------------------------------------------------
      // SUBSCRIPTION STATE
      // Set by SubscriptionProvider from get_my_subscription() RPC call.
      // -----------------------------------------------------------------------

      /** subscription_tier: 'demo' | 'starter' | 'pro' | 'club' */
      subscriptionTier: 'demo',

      /** subscription_status: 'active' | 'past_due' | 'canceled' | etc. */
      subscriptionStatus: 'active',

      /** active_tournament_count — denormalized counter from tenants table */
      activeTournamentCount: 0,

      // -----------------------------------------------------------------------
      // UI STATE
      // -----------------------------------------------------------------------

      /** Whether the sidebar is collapsed (desktop) */
      sidebarCollapsed: false,

      /** Current theme preference */
      theme: 'system',

      // -----------------------------------------------------------------------
      // ACTIONS
      // Prefixed with 'set' or 'clear' for clarity in DevTools.
      // -----------------------------------------------------------------------

      /**
       * setUser — called by AuthProvider when a session is established
       * @param {object} user - Supabase auth.User object
       */
      setUser: (user) => set({ user }, false, 'setUser'),

      /**
       * setProfile — called after fetching the public.users row
       * @param {object} profile - Row from public.users
       */
      setProfile: (profile) => set(
        { profile, role: profile?.role ?? null },
        false,
        'setProfile'
      ),

      /**
       * setTenant — called after fetching the tenant row
       * @param {object} tenant - Row from public.tenants
       */
      setTenant: (tenant) => set({ tenant }, false, 'setTenant'),

      /**
       * setSubscription — called by SubscriptionProvider from get_my_subscription()
       * @param {object} sub - { subscription_tier, subscription_status, active_tournament_count }
       */
      setSubscription: (sub) => set({
        subscriptionTier:       sub.subscription_tier,
        subscriptionStatus:     sub.subscription_status,
        activeTournamentCount:  sub.active_tournament_count,
      }, false, 'setSubscription'),

      /**
       * setSessionChecked — marks that the initial session check has completed.
       * Used by ProtectedRoute to distinguish "loading" from "not logged in".
       */
      setSessionChecked: (checked) => set({ sessionChecked: checked }, false, 'setSessionChecked'),

      /**
       * clearSession — wipes ALL session state on logout.
       * Must be called before supabase.auth.signOut() to ensure clean state.
       */
      clearSession: () => set({
        user:                   null,
        role:                   null,
        profile:                null,
        tenant:                 null,
        subscriptionTier:       'demo',
        subscriptionStatus:     'active',
        activeTournamentCount:  0,
        sessionChecked:         false,
      }, false, 'clearSession'),

      /**
       * toggleSidebar — collapses or expands the desktop sidebar
       */
      toggleSidebar: () => set(
        (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
        false,
        'toggleSidebar'
      ),

      /**
       * setTheme — updates theme preference ('light' | 'dark' | 'system')
       * @param {string} theme
       */
      setTheme: (theme) => set({ theme }, false, 'setTheme'),
    }),

    // DevTools label — appears in Redux DevTools extension
    { name: 'ArcusAppStore' }
  )
)
